// Street search for both views: any street in Sweden, without house numbers ("Postiljonsvägen, Enskede").
// Two sources, asked together: Photon (photon.komoot.io, OpenStreetMap data) finds streets that *start* with
// what's typed, so "hudding" already finds Huddingevägen, which Nominatim only matches on whole words; Nominatim
// adds its whole-word matches after them (and is the better one for an address with a house number, which
// kartvy's place search asks it for). The postort comes from the street's postcode
// (public/postcodePostort.json, built by the backend's scripts/build_postcode_postort.py), since both only
// give suburbs and towns ("Svedmyra", "Stockholm").

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const PHOTON_URL = 'https://photon.komoot.io/api/';
const SWEDEN_BBOX = '10.5,55.0,24.5,69.5';
const POSTCODE_POSTORT_URL = '/postcodePostort.json';
// Both return one result per street segment / postcode; ask for enough to cover a few towns.
const NOMINATIM_LIMIT = 30;
const PHOTON_LIMIT = 30;

let postcodePostortPromise = null;

// Postcode ("12247") → postort ("Enskede"). Loaded on the first street search; empty if it fails.
export const loadPostcodePostort = () => {
    if (!postcodePostortPromise) {
        postcodePostortPromise = fetch(POSTCODE_POSTORT_URL)
            .then((response) => (response.ok ? response.json() : null))
            .then((payload) => {
                const byPostcode = new Map();
                Object.entries(payload?.postorter || {}).forEach(([name, codes]) => {
                    (Array.isArray(codes) ? codes : []).forEach((code) => byPostcode.set(String(code), name));
                });
                return byPostcode;
            })
            .catch(() => new Map());
    }
    return postcodePostortPromise;
};

const resultCache = new Map();
const photonCache = new Map();

// Streets that start with the query (Photon): [{ road, postcode, town, lat, lng }], one per street segment.
export const searchStreetPrefix = async (query, signal) => {
    // Streets only, so a house number is left out ("storgatan 1 umeå" → "storgatan umeå"): Photon would
    // otherwise match it against postcodes.
    const key = String(query || '').trim().toLowerCase().split(/\s+/).filter((word) => !/\d/.test(word)).join(' ');
    if (!key) return [];
    if (photonCache.has(key)) return photonCache.get(key);
    const params = new URLSearchParams({ q: key, limit: String(PHOTON_LIMIT), layer: 'street', bbox: SWEDEN_BBOX });
    const response = await fetch(`${PHOTON_URL}?${params}`, { signal });
    if (!response.ok) throw new Error(`Street search failed: ${response.status}`);
    const payload = await response.json();
    const streets = (Array.isArray(payload?.features) ? payload.features : [])
        .map((feature) => {
            const props = feature?.properties || {};
            const [lng, lat] = feature?.geometry?.coordinates || [];
            const road = String(props.name || '').trim();
            if (props.countrycode !== 'SE' || !road || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
            // Photon's extent is [west, north, east, south].
            const [west, north, east, south] = Array.isArray(props.extent) ? props.extent : [];
            return {
                road,
                postcode: String(props.postcode || '').replace(/\D/g, ''),
                town: props.district || props.city || '',
                lat,
                lng,
                bounds: [west, north, east, south].every(Number.isFinite) ? [[south, west], [north, east]] : null,
            };
        })
        .filter(Boolean);
    photonCache.set(key, streets);
    return streets;
};

// Two lists taken in turn: [a1, b1, a2, b2, …].
export const interleave = (first, second) => {
    const out = [];
    for (let i = 0; i < Math.max(first.length, second.length); i += 1) {
        if (i < first.length) out.push(first[i]);
        if (i < second.length) out.push(second[i]);
    }
    return out;
};

// Streets matching the query: [{ road, postcode, town, lat, lng }], one per street segment; the ones that start
// with it first. With a house number typed the two take turns, Nominatim's first: it places the number, but
// ranks a street by its own measure ("sveavägen 10" gave Västerås and Falköping, not Stockholm). Either
// source failing leaves the other's.
export const searchStreets = async (query, signal) => {
    const [prefix, words] = await Promise.allSettled([searchStreetPrefix(query, signal), searchStreetWords(query, signal)]);
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (prefix.status === 'rejected' && words.status === 'rejected') throw words.reason;
    const prefixStreets = prefix.status === 'fulfilled' ? prefix.value : [];
    const wordStreets = words.status === 'fulfilled' ? words.value : [];
    return /\d/.test(String(query || '')) ? interleave(wordStreets, prefixStreets) : [...prefixStreets, ...wordStreets];
};

// Streets whose name has the query's words (Nominatim).
const searchStreetWords = async (query, signal) => {
    const key = String(query || '').trim().toLowerCase();
    if (!key) return [];
    if (resultCache.has(key)) return resultCache.get(key);
    const params = new URLSearchParams({
        format: 'jsonv2',
        countrycodes: 'se',
        addressdetails: '1',
        'accept-language': 'sv',
        layer: 'address',
        limit: String(NOMINATIM_LIMIT),
        q: key,
    });
    const response = await fetch(`${NOMINATIM_URL}?${params}`, { signal });
    if (!response.ok) throw new Error(`Street search failed: ${response.status}`);
    const payload = await response.json();
    const streets = (Array.isArray(payload) ? payload : [])
        .map((entry) => {
            const address = entry?.address || {};
            const road = String(address.road || (entry?.category === 'highway' ? entry?.name : '') || '').trim();
            const lat = Number(entry?.lat);
            const lng = Number(entry?.lon);
            if (!road || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
            return {
                road,
                postcode: String(address.postcode || '').replace(/\D/g, ''),
                town: address.town || address.city || address.village || address.hamlet || address.suburb || '',
                lat,
                lng,
            };
        })
        .filter(Boolean);
    resultCache.set(key, streets);
    return streets;
};
